import {
  type ComponentType,
  type ReactElement,
  useCallback,
  useRef,
} from 'react';

import type { ObjectWithId } from '../../types/common';
import type { MenuItem } from '../../ui/menus/menu/types';
import type { HDirection, VDirection } from '../../ui/popover/utils';
import PopoverTrigger, {
  type PopoverTriggerHandle,
} from '../../ui/popover_trigger/popover_trigger';
import type {
  ContextMenuContainerProps,
  MenuItemEvent,
} from '../context_menu/types';

export type ContextMenuWithOptions<
  Item extends ObjectWithId,
  State,
  Options,
> = {
  component: ComponentType<ContextMenuContainerProps<Item, State, Options>>;
} & (Options extends undefined ? object : { options: Options });

type Props<Item extends ObjectWithId, State, Options> = {
  contextMenu:
    | ComponentType<ContextMenuContainerProps<Item, State, Options>>
    | ContextMenuWithOptions<Item, State, Options>;
  contextItems?: Item[];
  showIcons?: boolean;
  onMenuItemClick?: (menuItem: MenuItem) => void;
  onAdditionalMenuItemClick?: (menuItem: MenuItem) => void;
  hDirection?: HDirection;
  vDirection?: VDirection;
  usePortal?: boolean;
  triggerElement: ReactElement;
  onOpen?: (handle: PopoverTriggerHandle) => void;
};

const TriggerContextMenu = <Item extends ObjectWithId, State, Options>(
  props: Props<Item, State, Options>,
): ReactElement => {
  const {
    contextMenu,
    contextItems,
    showIcons = true,
    onMenuItemClick,
    onAdditionalMenuItemClick,
    hDirection,
    vDirection,
    usePortal,
    triggerElement,
    onOpen,
  } = props;

  const emptyContextItems = useRef<Item[]>([]).current;
  const popoverTriggerHandle = useRef<PopoverTriggerHandle | null>(null);

  const handleOpen = useCallback(() => {
    if (onOpen && popoverTriggerHandle.current) {
      onOpen(popoverTriggerHandle.current);
    }
  }, [onOpen]);

  const handleMenuItemClick = useCallback(
    (menuItem: MenuItem, event: MenuItemEvent) => {
      if (menuItem.autoClose !== false && !event?.defaultPrevented) {
        popoverTriggerHandle.current?.close();
      }
      onMenuItemClick?.(menuItem);
    },
    [onMenuItemClick],
  );

  const handleAdditionalMenuItemClick = useCallback(
    (menuItem: MenuItem, event: MenuItemEvent) => {
      if (
        menuItem.additionalMenuItem?.autoClose !== false &&
        !event?.defaultPrevented
      ) {
        popoverTriggerHandle.current?.close();
      }
      onAdditionalMenuItemClick?.(menuItem);
    },
    [onAdditionalMenuItemClick],
  );

  const CustomContextMenuComponent =
    'component' in contextMenu ? contextMenu.component : contextMenu;
  const contextMenuOptions =
    'component' in contextMenu && 'options' in contextMenu
      ? contextMenu.options
      : undefined;

  return (
    <PopoverTrigger
      hDirectionHint={hDirection}
      vDirectionHint={vDirection}
      usePortal={usePortal}
      triggerElement={triggerElement}
      onOpen={handleOpen}
      ref={popoverTriggerHandle}
    >
      <CustomContextMenuComponent
        options={contextMenuOptions as Options}
        contextItems={contextItems ?? emptyContextItems}
        showIcons={showIcons}
        onMenuItemClick={handleMenuItemClick}
        onAdditionalMenuItemClick={handleAdditionalMenuItemClick}
      />
    </PopoverTrigger>
  );
};

export default TriggerContextMenu;
